import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { NISCO, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    attributeTypeValuesText?: TypographyPresentationProps;
    container?: any;
    titleStyle?: any;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    attributeTypeValuesText: {
        css: css`
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.title};
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${NISCO.TYPOGRAPHY.size.smaller};
            line-height: 24px;
            color: #333333;

            .list-wrapper {
                width: 100%;
            }

            .cols {
                display: flex;
                gap: 24px;
                align-items: flex-start;
            }

            .col {
                flex: 1;
                min-width: 0;
            }

            .cols-2 {
                width: 70%;
            }

            .col ul {
                list-style: disc;
                padding-left: 1.25rem;
                margin: 0;
            }

            @media (max-width: 640px) {
                .cols {
                    flex-direction: column;
                    gap: 12px;
                }
            }
        `,
    },
    container: {
        css: css`
            width: 100%;
            height: 100%;
            margin: 20px 0px;
            padding-left: 19px;
            padding-right: 19px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 32px 0px 24px 0px;
                        padding: 0px;
                    `,
                    css`
                        margin: 32px 0px 24px 0px;
                        padding: 0px;
                    `,
                    css`
                        margin: -32px;
                        padding: 24px 0px;
                        padding-top: 8px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    titleStyle: {
        css: css`
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.title};
            font-weight: 600;
            font-size: ${NISCO.TYPOGRAPHY.size.xxlarge};
            line-height: 40px;
            color: ${NISCO.COLORS.primaryColor};
            margin: 27px 0px 25px 0px;
            display: flex;
            gap: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        margin: 0px;
                        margin-bottom: 16px;
                        font-size: 24px;
                    `,
                    css`
                        flex-direction: column;
                        margin: 0px;
                        margin-bottom: 16px;
                        line-height: 32px;
                    `,
                    css`
                        margin: 27px 0px 16px 0px;
                        font-size: 24px;
                        line-height: 32px;
                        margin-top: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = specificationsStyles;

const renderSmartHtml = (htmlContent: string) => {
    if (!htmlContent) return null;

    // Guard against SSR: DOMParser only exists in browser
    if (typeof DOMParser === "undefined") {
        // Fallback for server-side rendering
        return parse(transformContentHtml(htmlContent), parserOptions);
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const ul = doc.querySelector("ul");
    let customClass = "";

    if (ul) {
        const items = Array.from(ul.querySelectorAll("li")).map(li => li.innerHTML.trim());
        const total = items.length;
        let columns: string[][] = [];

        if (total <= 7) {
            columns = [items];
        } else if (total <= 21) {
            const half = Math.ceil(total / 2);
            columns = [items.slice(0, half), items.slice(half)];
            customClass = "two-col-list";
        } else {
            const perCol = Math.ceil(total / 3);
            columns = [items.slice(0, perCol), items.slice(perCol, perCol * 2), items.slice(perCol * 2)];
        }

        return (
            <div className="list-wrapper">
                <div className={`cols cols-${columns.length}`}>
                    {columns.map((colItems, colIndex) => (
                        <div key={colIndex} className="col">
                            <ul className={customClass}>
                                {colItems.map((item, index) => (
                                    // eslint-disable-next-line react/no-danger
                                    <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Default: fallback to normal parsed HTML (handles <p>, <div>, <section>, etc.)
    return parse(transformContentHtml(htmlContent), parserOptions);
};

const NISProductDetailsSpecificationOverview: React.FC<Props> = ({ product: { specifications } }) => {
    if (!specifications) {
        return null;
    }

    const validSpecs = specifications.filter(item => item.htmlContent && item.htmlContent.trim() !== "");
    if (validSpecs.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            {validSpecs.map((item, index) => (
                <StyledWrapper key={item.name || index}>
                    <Typography {...styles.titleStyle} as="p">
                        {`${item.nameDisplay || item.name}`}
                    </Typography>

                    <Typography {...styles.attributeTypeValuesText} as="div">
                        {renderSmartHtml(item.htmlContent)}
                    </Typography>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(NISProductDetailsSpecificationOverview),
    definition: {
        displayName: "NIS Product Details Specification",
        group: "Product Details",
    },
};

export default widgetModule;
